import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectFavouritesSummary } from '@noths/polaris-client-user-favourites';

import {
  adaptStoreToNextPageURL,
  adaptStoreToPreviousPageURL,
} from 'src/adapters/storeToPageURL/adaptStoreToPaginationURL';
import { ProductPages } from 'src/components/organisms/ProductPages/components/ProductPages';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectTotalProducts } from 'src/redux/products/selectors/selectors';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { updatePageURLWithQuery } from 'src/redux/products/thunks/updatePageURLWithQuery';
import { TriggerName } from 'src/redux/products/types';
import { actions } from 'src/redux/productsQuery/slice';
import type { AppThunkDispatch } from 'src/redux/store';

/**
 * Everything this page reads from the store, named.
 *
 * Selecting the whole state re-rendered the listing on every dispatch, and the
 * listing dispatches one action per product as it scrolls into view (see the
 * visibilityTracking slice). Naming the values and comparing them shallowly means
 * an impression only reaches the tree when it has changed something the page shows.
 *
 * `productsByPage` and `favouriteProductCodes` are references straight out of the
 * store, so they compare by identity; everything else is a string, number or
 * boolean. The two URLs are rebuilt on every dispatch rather than being memoised,
 * which is a little string work against the whole grid re-rendering, and it keeps
 * the pagination adapters free to read whatever they need from the state.
 */
const selectProductPagesProps = (state: ReduxApplicationState) => ({
  favouriteProductCodes: selectFavouritesSummary(state).productCodes,
  isLoading: state.products.pageLoading !== null,
  loadMoreURL: adaptStoreToNextPageURL(state),
  loadPreviousURL: adaptStoreToPreviousPageURL(state),
  pageLoading: state.products.pageLoading,
  productsByPage: state.products.pages,
  totalProducts: selectTotalProducts(state),
});

export const ProductPagesContainer = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const {
    favouriteProductCodes,
    isLoading,
    loadMoreURL,
    loadPreviousURL,
    pageLoading,
    productsByPage,
    totalProducts,
  } = useSelector(selectProductPagesProps, shallowEqual);

  const onLoadPrevious = ({ page }: { page: number }) => {
    dispatch(actions.changePage(page - 1));
    dispatch(requestProducts({ triggerName: TriggerName.LOAD_PREVIOUS_PRODUCTS }));
    dispatch(updatePageURLWithQuery());
  };

  const onLoadMore = ({ page }: { page: number }) => {
    dispatch(actions.changePage(page + 1));
    dispatch(requestProducts({ triggerName: TriggerName.LOAD_MORE_PRODUCTS }));
    dispatch(updatePageURLWithQuery());
  };

  return (
    <ProductPages
      favouriteProductCodes={favouriteProductCodes}
      isLoading={isLoading}
      loadMoreURL={loadMoreURL}
      loadPreviousURL={loadPreviousURL}
      onLoadMore={onLoadMore}
      onLoadPrevious={onLoadPrevious}
      pageLoading={pageLoading}
      productsByPage={productsByPage}
      totalProducts={totalProducts}
    />
  );
};
